import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import moduleInfo from '../../config/module_info.json';
import { ChatbotService } from '../chatbot-services/chatbot.service';
import { LoggerService } from '../logger-services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class PrivilegesService {
  Privileges: any;
  private readonly PrivilegesServiceError = (error: any) =>
    `[PrivilegesService] - ${JSON.stringify(error)}`;
  constructor(private readonly chatbotService: ChatbotService, private readonly loggerService: LoggerService) { }

  // istanbul ignore next
  GetPrivilegesFromURF(URFModuleId: number, userId: number, domainId: number): Promise<string[]> {
    return new Promise((resolve, reject) => {
      this.chatbotService.GetFreyaPrivileges(userId).subscribe({
        next: (data: any) => {
          if (data && data.code === 200 && Array.isArray(data.data)) {
            let filteredData: any[] = [];
            if (URFModuleId != 0 && domainId != 0) {
              filteredData = data.data.filter((item: any) => item.module === URFModuleId.toString() && item.domain === domainId.toString());
            } else {
              filteredData = data.data;
            }
            const transformedData = filteredData.map((item: any) => item.name);
            resolve(transformedData);
          } else {
            resolve([]);
          }
        },
        error: (error) => {
          this.loggerService.logError(this.PrivilegesServiceError(error), "GetPrivilegesFromURF");
          reject(error);
        }
      });
    });
  }

  getPrivilegesList(
    userId: any,  URFModuleId: number = 0, domainId: number = 0
  ): Promise<{
    vectorDbDatasets: { name: string; value: string; default: boolean }[];
    cookiesArray: string[];
    agentPrivileges: string[];
  }> {
    let vectorDbDatasets: { name: string; value: string; default: boolean }[] = [];
    return new Promise((resolve, reject) => {
      if (!environment.IsDevelopment) {
        this.GetPrivilegesFromURF(URFModuleId, userId, domainId)
          .then((cookiesArray) => {
            this.processCookiesArray(cookiesArray, vectorDbDatasets);
            const agentPrivileges = this.filterAgentPrivileges(cookiesArray);
            resolve({ vectorDbDatasets, cookiesArray, agentPrivileges });
          })
          .catch((error) => {
            this.loggerService.logError(this.PrivilegesServiceError(error), "getPrivilegesList");
            reject(error);
          });
      } else {
        // istanbul ignore next
        try {
          const privilegeList = environment.Privileges;
          this.processCookiesArray(privilegeList, vectorDbDatasets);

          const agentPrivilagesList = environment.AgentPrivilagesList;
          const agentPrivileges = this.filterAgentPrivileges(agentPrivilagesList);
          resolve({ vectorDbDatasets, cookiesArray: privilegeList, agentPrivileges });
        } catch (error) {
          this.loggerService.logError(this.PrivilegesServiceError(error), "getPrivilegesList");
          reject(error);
        }
      }
    });
  }

  processCookiesArray(cookiesArray: string[], vectorDbDatasets: { name: string, value: string, default: boolean }[]): void {
    const specificModules: string[] = ["mdv", "mpr", "cos", "chp", "fds"];
    const currentDomain = (environment.DomainName || '').toLowerCase().trim();
    cookiesArray.forEach((item: any) => {
      // istanbul ignore next
      if ((item.startsWith('access') || item.startsWith('view_access')) && !item.endsWith('prompts')) {
        const lowerCaseItem = item.toLowerCase();
        const itemParts = lowerCaseItem.split('_');
        const lastPart = itemParts[itemParts.length - 1];
        if (specificModules.includes(lastPart)) {
          // v2.0: For any domain-suffixed privilege (e.g. view_access_to_regulations_cos, view_access_to_rtq_cos),
          // only add the module when the privilege's domain matches the current domain.
          if (lastPart !== currentDomain) {
            return;
          }
          item = itemParts.slice(0, -1).join('_');
        }
        let displayName = (moduleInfo as any)[item]?.DisplayName;
        if (displayName && !vectorDbDatasets.some(dataset => dataset.value === item)) {
          vectorDbDatasets.push({ name: displayName, value: item, default: false });
        }
      }
    });
    // v2.0: Show Regulations chatbot if user has gri.regulation.read (any domain) even without view_access_to_regulations_*
    const regulationsEntry = (moduleInfo as any)['view_access_to_regulations'];
    if (cookiesArray.some((p: string) => p === 'gri.regulation.read') && regulationsEntry?.DisplayName && !vectorDbDatasets.some(d => d.value === 'view_access_to_regulations')) {
      vectorDbDatasets.push({ name: regulationsEntry.DisplayName, value: 'view_access_to_regulations', default: false });
    }
  }

  /**
   * Filter privileges that contain "agent" in them
   * 
   * @param cookiesArray - Array of privilege strings to filter
   * @returns string[] - Filtered array containing only items with "agent"
   */
  filterAgentPrivileges(cookiesArray: string[]): string[] {
    try {
      const agentPrivileges = cookiesArray.filter(item => 
        item.toLowerCase().includes('agent')
      );
      
      this.loggerService.logInfo(`[PrivilegesService] Filtered agent privileges: ${agentPrivileges.length} out of ${cookiesArray.length}`, "filterAgentPrivileges");
      
      return agentPrivileges;
    } catch (error) {
      this.loggerService.logError(this.PrivilegesServiceError(error), "filterAgentPrivileges");
      return [];
    }
  }
}